import { useMutation, useQuery, useQueryClient, } from "@tanstack/react-query";
import { CACHE_FRESH_TIME, CACHE_RETENTION_TIME } from "./constants";
const usePublishQuery = (options) => {
    return useQuery({
        staleTime: CACHE_FRESH_TIME,
        gcTime: CACHE_RETENTION_TIME,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        ...options,
    });
};
const getForecastChangesQueryKey = (params) => {
    const { unitId, divisionId, startDate, endDate, changeType } = params;
    return [
        "forecast-changes",
        unitId,
        divisionId,
        startDate,
        endDate,
        changeType,
    ];
};
const getForecastChangesQueryFn = (fetchApi, params, signal) => {
    return async () => {
        const { unitId, divisionId, startDate, endDate, changeType } = params;
        const queryParams = new URLSearchParams();
        queryParams.append("unitId", unitId);
        if (divisionId !== undefined && divisionId !== "") {
            queryParams.append("divisionId", divisionId);
        }
        queryParams.append("startDate", startDate);
        queryParams.append("endDate", endDate);
        queryParams.append("changeType", changeType);
        try {
            const res = await fetchApi.jsonInJsonOut(`/forecast/changes?${queryParams.toString()}`, {
                signal,
            });
            return res;
        }
        catch (error) {
            console.error("Error fetching forecast changes: %o", error);
            return undefined;
        }
    };
};
export const useGetForecastChanges = (fetchApi, params, signal) => {
    const { unitId, startDate, endDate } = params;
    const queryKey = getForecastChangesQueryKey(params);
    const { data, refetch, isLoading } = usePublishQuery({
        queryKey,
        enabled: Boolean(unitId) && Boolean(startDate) && Boolean(endDate),
        queryFn: getForecastChangesQueryFn(fetchApi, params, signal),
    });
    return {
        data,
        isLoading,
        refetch,
    };
};
const getAdjustmentReasonsQueryFn = (fetchApi, signal) => {
    return async () => {
        try {
            const res = await fetchApi.jsonInJsonOut(`/adjustment-reasons`, {
                signal,
            });
            return res;
        }
        catch (error) {
            console.error("Error fetching adjustment reasons: %o", error);
            return undefined;
        }
    };
};
export const useGetAdjustmentReasons = (fetchApi, signal) => {
    const queryKey = ["adjustment-reasons"];
    const { data, refetch, isLoading } = usePublishQuery({
        queryKey,
        queryFn: getAdjustmentReasonsQueryFn(fetchApi, signal),
    });
    return {
        data,
        isLoading,
        refetch,
    };
};
const publishForecastsRequest = async (fetchApi, params) => {
    const { driverAdjustments, startDate, endDate, source } = params;
    const queryParams = new URLSearchParams();
    queryParams.append("source", source);
    const requestBody = {
        driverAdjustments: Array.from(driverAdjustments),
        startDate,
        endDate,
    };
    await fetchApi.jsonInVoidOut(`/forecast/publish?${queryParams.toString()}`, {
        method: "PATCH",
        body: JSON.stringify(requestBody),
    });
};
export const usePublishForecasts = (fetchApi, options) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (paramsList) => {
            await Promise.all(paramsList.map((params) => publishForecastsRequest(fetchApi, params)));
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["forecast-changes"] });
            queryClient.invalidateQueries({ queryKey: ["day-forecast"] });
            queryClient.invalidateQueries({ queryKey: ["week-forecast"] });
            queryClient.invalidateQueries({ queryKey: ["year-forecast"] });
            sessionStorage.setItem("clearPlantaHeadcountCache", "true");
            options?.onSuccess?.();
        },
        onError: (error) => {
            console.error("Error publishing forecasts: %o", error);
        },
    });
};
const resetForecastsRequest = async (fetchApi, params) => {
    const { driverAdjustments, startDate, endDate } = params;
    await fetchApi.jsonInVoidOut(`/forecast/draft`, {
        method: "PATCH",
        body: JSON.stringify({
            driverAdjustments: Array.from(driverAdjustments),
            startDate,
            endDate,
        }),
    });
};
export const useResetForecasts = (fetchApi, options) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (params) => resetForecastsRequest(fetchApi, params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["forecast-changes"] });
            queryClient.invalidateQueries({ queryKey: ["day-forecast"] });
            queryClient.invalidateQueries({ queryKey: ["week-forecast"] });
            queryClient.invalidateQueries({ queryKey: ["year-forecast"] });
            options?.onSuccess?.();
        },
        onError: (error) => {
            console.error("Error resetting forecasts: %o", error);
        },
    });
};
//# sourceMappingURL=publishApi.js.map